import React from 'react';
import { formatPlural } from '../../../utils/string.utils';
import { ChannelSubscriberKind } from '../../../shared/global-enums';
import { Channel, ChannelSubscriptionType, ChannelTypes } from '../../../shared/types';
import StatCards from '../../ui/StatCards';
import {
	isActivePaidSubscription
} from '../../../utils/channelSubscriptions.utils';

export interface SubscriberStatsPanelProps {
	channel: Channel;
	channelSubscriberKind?: ChannelSubscriberKind;
}

export const SubscriberStatsPanel = ({ channel, channelSubscriberKind }: SubscriberStatsPanelProps): JSX.Element => {
	const totalUsers = channel.channelActiveUsersCount || 0;

	const payingUserCount = channel
		.channelSubscribedUsers
		.reduce((previousValue, currentUser) => {
			const hasPaid = currentUser
				.channelSubscriptions
				.some(channelSubscription => channelSubscription.channelId === channel._id &&
					isActivePaidSubscription(channelSubscription));

			return previousValue + (hasPaid ? 1 : 0);
		}, 0);

	const pendingTrialUserCount = channel
		.channelSubscribedUsers
		.reduce((previousValue, currentUser) => {
			const hasActiveTrial = currentUser
				.channelSubscriptions
				.some(channelSubscription => channelSubscription.channelId === channel._id &&
					channelSubscription.subscriptionType === ChannelSubscriptionType.Trial &&
					channelSubscription.subscriptionState === 'pending');

			return previousValue + (hasActiveTrial ? 1 : 0);
		}, 0);

	const totalUsersTitle = channel.type === ChannelTypes.premium ? 'Total users' : 'Active users';
	const totalUsersMessage = `${formatPlural(totalUsers, 'subscriber')}`;
	const payingTitle = 'Paying users';
	const payingMessage = `${formatPlural(payingUserCount, 'paying subscriber')}`;
	const pendingTitle = 'Pending requests';

	const pendingTrialMessage = pendingTrialUserCount === 0
		? 'No requests'
		: `${formatPlural(pendingTrialUserCount, 'request')}`;

	const articleCountTitle = 'Content published to date';
	const articleCount = channel?.articleCount || 0;
	const articleCountMessage = formatPlural(articleCount, 'article');

	const pendingRequests = channel.channelSubscribedUsers
		.map(u => !!u.channelSubscriptions.find(cs => cs.channelId === channel._id && cs.subscriptionState === 'pending'))
		.filter(x => x).length;

	const items = [
		{
			label: totalUsersTitle,
			value: totalUsersMessage
		},
		...(channel.type === ChannelTypes.premium ? [
			channelSubscriberKind === ChannelSubscriberKind.Trial ? {
				label: pendingTitle,
				value: pendingTrialMessage
			} : {
				label: payingTitle,
				value: payingMessage
			}] : []),
		...(channel.type === ChannelTypes.freeClosed ? [{
			label: pendingTitle,
			value: formatPlural(pendingRequests, 'request')
		}] : []),
		{
			label: articleCountTitle,
			value: articleCountMessage
		}
	]

	return (
		<StatCards
			items={items}
		/>
	);
}

export default SubscriberStatsPanel;
